import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useGraphql } from "~/api-client";
import type { GetActivityQuery } from "~/graphql-client/generated";
import type { CartRecord } from "~/types";

const PAGE_SIZE = 10;

/** Activity Types */
export interface ActivitySummary {
  points: number;
  credit: number;
  itemCount: number;
}

interface BaseActivity {
  __typename:
    | "CreditExpiredActivity"
    | "MessageActivity"
    | "SpendActivity"
    | "ItemExpiredActivity"
    | "PromotionActivity"
    | "PointsActivity";
  /** Unique ID for this item */
  id: string;
  title: string;
  /** Location name where applicable */
  locationName?: string | null;
  /** Timestamp for the event */
  timestamp: string;
}

export interface CreditExpiredActivity extends BaseActivity {
  __typename: "CreditExpiredActivity";
  credit: Credit;
}
export interface ItemExpiredActivity extends BaseActivity {
  __typename: "ItemExpiredActivity";
  item: Item;
}

export interface PromotionActivity extends BaseActivity {
  __typename: "PromotionActivity";
  promotion: Promotion;
  promotionOrder?: ActivityOrder | null;
  promotionCredit?: Credit | null;
  promotionItem?: Item | null;
  points: number;
  pointsText: string;
}

export interface MessageActivity extends BaseActivity {
  __typename: "MessageActivity";
  body: string;
}

export interface SpendActivity extends BaseActivity {
  __typename: "SpendActivity";
  order: SpendActivityOrder;
  pointsText: string;
  points: number;
  cart?: CartRecord | null;
}

export interface PointsActivity extends BaseActivity {
  __typename: "PointsActivity";
  points: number;
  pointsText: string;
}

export type Activity =
  | CreditExpiredActivity
  | ItemExpiredActivity
  | MessageActivity
  | PromotionActivity
  | SpendActivity
  | PointsActivity;

/** Other types */

interface ActivityOrder {
  event: PosEvent;
  id: string;
  deliveryAddress?: {
    apartment?: string | null;
    streetAddress: string;
    locality: string;
    region: string;
    country: string;
    postcode: string;
    coords: {
      lat: number;
      lng: number;
    };
  } | null;
  serviceRef?: string | null;
  billingType?: string | null;
  refId: string;
  total: number;
  tax?: number | null;
  itemTotal: number;
  itemTax?: number | null;
  tipAmount?: number | null;
  creditAmount: number;
  customerFeeAmount: number;

  currency: string;
  items?: SpendActivityItem[];
  discounts?: SpendActivityModifier[] | null;
  fulfillAt?: string | null;
  estimatedReadyAt?: string | null;
  deliveryAtMin?: string | null;
  deliveryAtMax?: string | null;
}

interface SpendActivityOrder extends ActivityOrder {
  externalAmount: number;
  paymentId?: string | null;
  paymentMethodType?: string | null;
  paymentMethodName?: string | null;
  paymentMethodIconUrl?: string | null;
}

interface Credit {
  id: string;
  title: string;
  amount: number;
  remaining: number;
  created: string;
  expires: string;
}

interface Item {
  name?: string | null;
  expires?: string | null;
  claimed?: string | null;
  claimedLocation?: string | null;
  imageUrl?: string | null;
}

export interface PosEvent {
  tone: Tone;
  message: string;
}

interface Promotion {
  title: string;
  details: string;
  price?: number | null;
  priceType?: "DOLLARS" | "POINTS" | "NONE" | null;
}

interface SpendActivityItem {
  amount: number;
  tax?: number | null;
  name: string;
  quantity: number;
  options?: SpendActivityOption[] | null;
}

type SpendActivityModifier = {
  amount: number;
  tax?: number | null;
  name: string;
} | null;

type SpendActivityOption = {
  name: string;
  choices: SpendActivityChoice[];
} | null;

type SpendActivityChoice = {
  name: string;
  amount: number;
  tax?: number | null;
} | null;

export type Tone = "IN_PROGRESS" | "COMPLETED" | "FAILED";

interface ActivityHookResult {
  activity: Activity[];
  isValidating: boolean;
  isLoading: boolean;
  reachedEnd: boolean;
  summary: ActivitySummary;
  size: number;
  revalidate: () => void;
  setSize: (
    size: number | ((_size: number) => number),
  ) => Promise<GetActivityQuery[] | undefined>;
}

export function useActivity(options?: {
  revalidateIfStale?: boolean;
}): ActivityHookResult {
  const { getActivity, getActivitySummary } = useGraphql();

  const {
    data: activityPageData,
    isFetching: isValidating,
    isLoading,
    fetchNextPage,
    refetch: mutate,
  } = useInfiniteQuery({
    queryKey: ["activity"],
    queryFn: ({ pageParam }) => getActivity(pageParam || ""),
    getNextPageParam: (lastPage) => {
      if (lastPage?.activity.pageInfo.endCursor) {
        return lastPage.activity.pageInfo.endCursor;
      }
      return undefined;
    },
    initialPageParam: "",
    refetchOnWindowFocus: false,
    staleTime: options?.revalidateIfStale === false ? Infinity : 0,
  });

  const { data: summaryData } = useQuery({
    queryKey: ["summary"],
    queryFn: getActivitySummary,
    refetchOnWindowFocus: false,
    staleTime: options?.revalidateIfStale === false ? Infinity : 0,
  });

  const activity: Activity[] = activityPageData
    ? activityPageData.pages.reduce((acc, activityGroup) => {
        if (activityGroup.activity.items)
          acc = acc.concat(activityGroup.activity.items);
        return acc;
      }, [] as Activity[])
    : [];

  const reachedEnd: boolean = activityPageData
    ? activityPageData.pages[activityPageData.pages.length - 1]?.activity.items
        .length < PAGE_SIZE
    : false;

  const size = activityPageData?.pages.length ?? 0;

  const setSize = async (size: number | ((size: number) => number)) => {
    const targetSize =
      typeof size === "function"
        ? size(activityPageData?.pages.length ?? 0)
        : size;
    const currentSize = activityPageData?.pages.length ?? 0;

    if (targetSize > currentSize) {
      await fetchNextPage();
    }
    return activityPageData?.pages;
  };

  return {
    activity,
    isValidating,
    isLoading,
    reachedEnd,
    summary: summaryData ?? { points: 0, credit: 0, itemCount: 0 },
    size,
    revalidate: mutate,
    setSize,
  };
}

interface ActivitySummaryHookResult {
  summary: ActivitySummary;
  revalidate: () => void;
  isLoading: boolean;
}

export function useActivitySummary(options?: {
  revalidateIfStale?: boolean;
}): ActivitySummaryHookResult {
  const { getActivitySummary } = useGraphql();

  const {
    data: summaryData,
    refetch: revalidate,
    isLoading,
  } = useQuery({
    queryKey: ["summary"],
    queryFn: getActivitySummary,
    refetchOnWindowFocus: false,
    staleTime: options?.revalidateIfStale === false ? Infinity : 0,
  });

  return {
    summary: summaryData ?? { points: 0, credit: 0, itemCount: 0 },
    revalidate,
    isLoading,
  };
}
